import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './tenure.reducer';

export const Tenure = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const tenureList = useAppSelector(state => state.tenure.entities);
  const loading = useAppSelector(state => state.tenure.loading);
  const totalItems = useAppSelector(state => state.tenure.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="tenure-heading" data-cy="TenureHeading">
        <Translate contentKey="apiLocationApp.tenure.home.title">Tenures</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.tenure.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link to="/tenure/new" className="btn btn-primary jh-create-entity" id="jh-create-entity" data-cy="entityCreateButton">
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.tenure.home.createLabel">Create new Tenure</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {tenureList && tenureList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.tenure.id">ID</Translate> <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.tenure.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.tenure.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.tenure.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.tenure.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalAllHouseholds')}>
                  <Translate contentKey="apiLocationApp.tenure.totalAllHouseholds">Total All Households</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalAllHouseholds')} />
                </th>
                <th className="hand" onClick={sort('ownedOutright')}>
                  <Translate contentKey="apiLocationApp.tenure.ownedOutright">Owned Outright</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ownedOutright')} />
                </th>
                <th className="hand" onClick={sort('ownedMortgageLoan')}>
                  <Translate contentKey="apiLocationApp.tenure.ownedMortgageLoan">Owned Mortgage Loan</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('ownedMortgageLoan')} />
                </th>
                <th className="hand" onClick={sort('sharedOwnership')}>
                  <Translate contentKey="apiLocationApp.tenure.sharedOwnership">Shared Ownership</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('sharedOwnership')} />
                </th>
                <th className="hand" onClick={sort('socialRentedCouncil')}>
                  <Translate contentKey="apiLocationApp.tenure.socialRentedCouncil">Social Rented Council</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('socialRentedCouncil')} />
                </th>
                <th className="hand" onClick={sort('socialRentedOther')}>
                  <Translate contentKey="apiLocationApp.tenure.socialRentedOther">Social Rented Other</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('socialRentedOther')} />
                </th>
                <th className="hand" onClick={sort('privateRented')}>
                  <Translate contentKey="apiLocationApp.tenure.privateRented">Private Rented</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('privateRented')} />
                </th>
                <th className="hand" onClick={sort('livesRentFree')}>
                  <Translate contentKey="apiLocationApp.tenure.livesRentFree">Lives Rent Free</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('livesRentFree')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {tenureList.map((tenure, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/tenure/${tenure.id}`} color="link" size="sm">
                      {tenure.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${tenure.geographicLevel}`} />
                  </td>
                  <td>{tenure.date}</td>
                  <td>{tenure.geography}</td>
                  <td>{tenure.geographyCode}</td>
                  <td>{tenure.totalAllHouseholds}</td>
                  <td>{tenure.ownedOutright}</td>
                  <td>{tenure.ownedMortgageLoan}</td>
                  <td>{tenure.sharedOwnership}</td>
                  <td>{tenure.socialRentedCouncil}</td>
                  <td>{tenure.socialRentedOther}</td>
                  <td>{tenure.privateRented}</td>
                  <td>{tenure.livesRentFree}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button tag={Link} to={`/tenure/${tenure.id}`} color="info" size="sm" data-cy="entityDetailsButton">
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/tenure/${tenure.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/tenure/${tenure.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.tenure.home.notFound">No Tenures found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={tenureList && tenureList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default Tenure;
